<template>
  <span class="ani-refresher" ref="refresher">
    <IconCustom
      ref="iconRef"
      ligature="refresh"
      class="ani-refresher-icon ani-text-large ani-color-background-primary ani-border-small text-main-background"
    ></IconCustom>
  </span>
</template>

<script setup lang="ts">
import { onActivated, onDeactivated, onMounted, onUnmounted, toRefs, useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useUiStore } from '@/stores/ui.store.ts'
import IconCustom from '@/components/IconCustom.vue'

const props = defineProps<{
  scrollTop: number
}>()

const emit = defineEmits<{
  (e: 'refresh'): void
}>()

const { scrollTop } = toRefs(props)

const refresher = useTemplateRef('refresher')

const uiStore = useUiStore()
const { openDialogCount, horizontalScrolling } = storeToRefs(uiStore)

let triggerStartY = 0
let triggerHeight = 0
let triggerProgress = 0
let isDragging = false
let currentRotation = 0
let spinFrameId: number | null = null
const triggerProgressThreshold = 0.95
const DISMISS_DURATION = 400
const REFRESH_DURATION = 800
const THRESHOLD_SNAP_DURATION = 150

function addTouchListener() {
  document.removeEventListener('touchstart', touchStartListener)
  document.addEventListener('touchstart', touchStartListener, { passive: true })
}

function removeTouchListeners() {
  document.removeEventListener('touchstart', touchStartListener)
  document.removeEventListener('touchend', touchEndListener)
  document.removeEventListener('touchmove', touchMoveListener)
  document.body.classList.remove('refresh-gesture-active')
}

onMounted(addTouchListener)
onActivated(addTouchListener)
onDeactivated(removeTouchListeners)
onUnmounted(removeTouchListeners)

const touchStartListener = (evt: TouchEvent) => {
  if (scrollTop.value === 0 && openDialogCount.value === 0 && !horizontalScrolling.value) {
    document.addEventListener('touchend', touchEndListener)
    document.addEventListener('touchmove', touchMoveListener)
    triggerStartY = evt.touches[0].screenY
    triggerHeight = 100
    triggerProgress = 0
    isDragging = true
    requestAnimationFrame(triggerAnimation)
  }
}

const touchEndListener = () => {
  document.removeEventListener('touchend', touchEndListener)
  document.removeEventListener('touchmove', touchMoveListener)
  isDragging = false
  document.body.classList.remove('refresh-gesture-active')

  if (triggerProgress >= triggerProgressThreshold) {
    animateRefreshAndDismiss()
  } else {
    animateDismiss()
  }
}

const DRAG_OFFSET = 10

const touchMoveListener = (evt: TouchEvent) => {
  if (horizontalScrolling.value) {
    isDragging = false
    document.body.classList.remove('refresh-gesture-active')
    document.removeEventListener('touchend', touchEndListener)
    document.removeEventListener('touchmove', touchMoveListener)
    animateDismiss()
    return
  }
  const delta = Math.max(0, evt.changedTouches[0].screenY - triggerStartY - DRAG_OFFSET)
  if (delta > 0) {
    document.body.classList.add('refresh-gesture-active')
  }
  const clamped = Math.min(delta, triggerHeight)
  const newTriggerProgress = clamped / triggerHeight
  const overflow = Math.max(0, delta - triggerHeight)
  const extraY = overflow * 0.3
  currentRotation = (delta / triggerHeight) * 360

  if (Math.abs(newTriggerProgress - triggerProgress) >= 0.005 || overflow > 0) {
    triggerProgress = newTriggerProgress
    requestAnimationFrame(() => triggerAnimation(extraY))
  }
}

function setVars(
  el: HTMLElement,
  top: number,
  scale: number,
  iconOpacity: number,
  iconRotation?: number,
) {
  el.style.setProperty('--top', String(top))
  el.style.setProperty('--scale', String(scale))
  el.style.setProperty('--icon-opacity', String(iconOpacity))
  if (iconRotation !== undefined) {
    el.style.setProperty('--icon-rotation', String(iconRotation))
  }
}

function triggerAnimation(extraY = 0) {
  const el = refresher.value
  if (!el) return

  if (!isDragging) return

  const pastThreshold = triggerProgress >= triggerProgressThreshold
  const top = triggerProgress * triggerHeight + extraY
  const iconOpacity = pastThreshold ? 1 : triggerProgress * 0.5

  el.classList.add('visible')
  el.classList.toggle('past-threshold', pastThreshold)
  setVars(el, top, triggerProgress, iconOpacity, currentRotation)
}

function startSpin() {
  const degreesPerMs = 360 / REFRESH_DURATION
  let lastTime = performance.now()

  function tick(now: number) {
    const el = refresher.value
    if (!el) return
    currentRotation += degreesPerMs * (now - lastTime)
    lastTime = now
    el.style.setProperty('--icon-rotation', String(currentRotation))
    spinFrameId = requestAnimationFrame(tick)
  }

  spinFrameId = requestAnimationFrame(tick)
}

function stopSpin() {
  if (spinFrameId !== null) {
    cancelAnimationFrame(spinFrameId)
    spinFrameId = null
  }
}

function animateDismiss() {
  const el = refresher.value
  if (!el) return

  el.classList.remove('past-threshold')
  el.classList.add('dismissing')
  setVars(el, 0, 0, 0)

  setTimeout(() => {
    el.classList.remove('visible', 'dismissing')
    triggerProgress = 0
  }, DISMISS_DURATION)
}

function animateRefreshAndDismiss() {
  const el = refresher.value
  if (!el) return

  const thresholdTop = triggerProgressThreshold * triggerHeight
  el.classList.add('snapping')
  startSpin()

  requestAnimationFrame(() => {
    setVars(el, thresholdTop, 1, 1)
  })

  emit('refresh')

  setTimeout(() => {
    el.classList.remove('snapping', 'past-threshold')
    el.classList.add('dismissing')
    setVars(el, thresholdTop, 0, 0)

    setTimeout(() => {
      stopSpin()
      el.classList.remove('visible', 'dismissing')
      triggerProgress = 0
    }, DISMISS_DURATION)
  }, REFRESH_DURATION)
}
</script>

<style lang="scss" scoped>
.ani-refresher {
  --dismiss-duration: v-bind(DISMISS_DURATION + 'ms');
  --threshold-snap-duration: v-bind(THRESHOLD_SNAP_DURATION + 'ms');

  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%) scale(0);
  z-index: 9999;
  display: none;
  background-color: var(--color-text);
  line-height: 1;
  height: 35px;
  width: 35px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  font-size: 30px;

  &.visible {
    display: flex;
    top: calc(var(--top) * 1px);
    transform: translateX(-50%) scale(var(--scale));
  }

  &.snapping {
    transition: top var(--threshold-snap-duration) cubic-bezier(0.16, 1, 0.3, 1);
  }

  &.dismissing {
    transition:
      transform var(--dismiss-duration) cubic-bezier(0.16, 1, 0.3, 1),
      top var(--dismiss-duration) cubic-bezier(0.16, 1, 0.3, 1);

    .ani-refresher-icon {
      transition: opacity var(--dismiss-duration) cubic-bezier(0.16, 1, 0.3, 1);
    }
  }

  &.past-threshold .ani-refresher-icon {
    transition: opacity var(--threshold-snap-duration) ease;
  }
}

.ani-refresher-icon {
  opacity: var(--icon-opacity, 0);
  transform: rotate(calc(var(--icon-rotation, 0) * 1deg));
}
</style>

<style lang="scss">
body.refresh-gesture-active > * {
  pointer-events: none;
}
</style>
